import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Plus, Star, Trash2 } from "lucide-react";

import {
  WorkspacesService,
  type Workspace,
  type WorkspaceMembership,
  type WorkspaceMembershipListResponse,
  type WorkspaceRole,
  type WorkspaceStatus,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useActiveWorkspace } from "@/hooks/use-active-workspace";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { ROUTES } from "@/lib/constants";
import { formatDateID } from "@/lib/format";
import {
  WORKSPACE_ROLE_TONE,
  WORKSPACE_STATUS_TONE,
} from "@/lib/workspace-helpers";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/shared/page-header";
import { Section } from "@/components/shared/section";
import { AddMemberDialog } from "@/components/workspaces/add-member-dialog";
import { ArchiveWorkspaceDialog } from "@/components/workspaces/archive-workspace-dialog";
import { cn } from "@/lib/utils";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);
const ROLE_OPTIONS: ReadonlyArray<WorkspaceRole> = [
  "workspace_admin",
  "workspace_member",
  "workspace_viewer",
];

// Lifecycle + role identifiers map to translation keys so the
// badges + selects re-render on a locale switch.
const WORKSPACE_STATUS_KEY: Record<WorkspaceStatus, TranslationKey> = {
  active: "workspaces.status.active",
  archived: "workspaces.status.archived",
};

const WORKSPACE_ROLE_KEY: Record<WorkspaceRole, TranslationKey> = {
  workspace_admin: "workspaces.roles.workspace_admin",
  workspace_member: "workspaces.roles.workspace_member",
  workspace_viewer: "workspaces.roles.workspace_viewer",
};

export default function WorkspaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isTenantAdmin = user ? TENANT_ADMIN_ROLES.has(user.role) : false;
  const { workspaces, refreshWorkspaces } = useActiveWorkspace();

  // Backend ships ``role`` + ``status`` as plain strings; only
  // translate the canonical values and fall back to the raw value.
  const resolveRoleLabel = (role: WorkspaceRole | string): string => {
    const key = WORKSPACE_ROLE_KEY[role as WorkspaceRole];
    return key ? t(key) : role;
  };
  const resolveStatusLabel = (status: string): string => {
    const key = WORKSPACE_STATUS_KEY[status as WorkspaceStatus];
    return key ? t(key) : status;
  };

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const detailFetcher = useCallback(() => {
    if (!id) throw new Error("Workspace ID is required.");
    return WorkspacesService.get(id);
  }, [id]);
  const detail = useApi<Workspace>(detailFetcher, { enabled: !!id });

  const membersFetcher = useCallback(() => {
    if (!id) throw new Error("Workspace ID is required.");
    return WorkspacesService.listMembers(id, { limit: 200 });
  }, [id]);
  const members = useApi<WorkspaceMembershipListResponse>(membersFetcher, {
    enabled: !!id,
  });

  const setDefault = useMutation<string, Workspace>(
    (wsId) => WorkspacesService.setDefault(wsId),
    {
      onSuccess: () => {
        void detail.refetch();
        void refreshWorkspaces();
      },
    }
  );

  const updateRole = useMutation<
    { userId: string; role: WorkspaceRole },
    WorkspaceMembership
  >(
    ({ userId, role }) =>
      WorkspacesService.updateMemberRole(id!, userId, { role }),
    { onSuccess: () => void members.refetch() }
  );

  const removeMember = useMutation<string, void>(
    (userId) => WorkspacesService.removeMember(id!, userId),
    { onSuccess: () => void members.refetch() }
  );

  const workspace = detail.data;
  const memberItems = useMemo(() => members.data?.items ?? [], [members.data]);

  // workspace_admin gate: caller is tenant admin OR has the
  // workspace_admin role on THIS workspace.
  const callerMembership = memberItems.find((m) => m.user_id === user?.user_id);
  const canManageMembers =
    isTenantAdmin || callerMembership?.role === "workspace_admin";

  if (detail.isLoading && !workspace) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (detail.error && !workspace) {
    return (
      <div className="space-y-4">
        <Alert variant="danger">{parseApiError(detail.error)}</Alert>
        <Button variant="outline" onClick={() => navigate(ROUTES.WORKSPACES)}>
          <ArrowLeft size={14} />
          {t("workspaces.detail.backToList")}
        </Button>
      </div>
    );
  }

  if (!workspace) return null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={ROUTES.WORKSPACES}
          className="inline-flex items-center gap-1 text-xs text-ocean-600 hover:text-ocean-800"
        >
          <ArrowLeft size={12} />
          {t("workspaces.detail.backToWorkspaces")}
        </Link>
      </div>

      <PageHeader
        title={workspace.name}
        description={
          workspace.description ?? t("workspaces.detail.noDescription")
        }
        action={
          isTenantAdmin && (
            <div className="flex gap-2">
              {workspace.status === "active" && !workspace.is_default && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void setDefault.mutate(workspace.id)}
                  disabled={setDefault.isLoading}
                >
                  <Star size={14} />
                  {t("workspaces.detail.setDefault")}
                </Button>
              )}
              {workspace.status === "active" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setArchiveOpen(true)}
                >
                  <Archive size={14} />
                  {t("workspaces.detail.archive")}
                </Button>
              )}
            </div>
          )
        }
      />

      {setDefault.error && (
        <Alert variant="danger">{parseApiError(setDefault.error)}</Alert>
      )}
      {updateRole.error && (
        <Alert variant="danger">{parseApiError(updateRole.error)}</Alert>
      )}
      {removeMember.error && (
        <Alert variant="danger">{parseApiError(removeMember.error)}</Alert>
      )}

      <Section
        title={t("workspaces.detail.metadataTitle")}
        description={t("workspaces.detail.metadataDescription")}
      >
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-ocean-100 bg-white p-5 tablet:grid-cols-3">
          <Field label={t("workspaces.detail.fieldSlug")}>
            <code className="text-xs text-ocean-700">{workspace.slug}</code>
          </Field>
          <Field label={t("workspaces.detail.fieldStatus")}>
            <Badge tone={WORKSPACE_STATUS_TONE[workspace.status] ?? "neutral"}>
              {resolveStatusLabel(workspace.status)}
            </Badge>
          </Field>
          <Field label={t("workspaces.detail.fieldDefault")}>
            {workspace.is_default ? (
              <Badge tone="ocean">{t("workspaces.badges.defaultYes")}</Badge>
            ) : (
              <span className="text-sm text-neutral-500">
                {t("workspaces.badges.defaultNo")}
              </span>
            )}
          </Field>
          <Field label={t("workspaces.detail.fieldMemberCount")}>
            <span className="text-sm text-ocean-900">
              {workspace.member_count}
            </span>
          </Field>
          <Field label={t("workspaces.detail.fieldCreated")}>
            <span className="text-sm text-neutral-700">
              {formatDateID(workspace.created_at)}
            </span>
          </Field>
          <Field label={t("workspaces.detail.fieldUpdated")}>
            <span className="text-sm text-neutral-700">
              {formatDateID(workspace.updated_at)}
            </span>
          </Field>
        </div>
      </Section>

      <Section
        title={t("workspaces.detail.membersTitle")}
        description={t("workspaces.detail.membersDescription")}
        action={
          canManageMembers && workspace.status === "active" ? (
            <Button onClick={() => setAddMemberOpen(true)} size="sm">
              <Plus size={14} />
              {t("workspaces.detail.addMember")}
            </Button>
          ) : undefined
        }
      >
        {members.error && (
          <Alert variant="warning">{parseApiError(members.error)}</Alert>
        )}
        <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("workspaces.detail.memberName")}</TableHead>
                <TableHead>{t("workspaces.detail.memberEmail")}</TableHead>
                <TableHead>{t("workspaces.detail.memberRole")}</TableHead>
                <TableHead className="hidden tablet:table-cell">
                  {t("workspaces.detail.memberJoined")}
                </TableHead>
                <TableHead className="text-right">
                  {t("workspaces.detail.memberActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.isLoading && memberItems.length === 0 && (
                <>
                  {[0, 1].map((i) => (
                    <TableRow key={`m-sk-${i}`}>
                      <TableCell colSpan={5} className="py-4">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!members.isLoading && memberItems.length === 0 && (
                <TableEmpty
                  colSpan={5}
                  message={t("workspaces.detail.membersEmpty")}
                />
              )}
              {memberItems.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium text-ocean-900">
                    {m.user_full_name || (
                      <span className="text-neutral-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-neutral-700">
                    {m.user_email}
                  </TableCell>
                  <TableCell>
                    {canManageMembers ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          void updateRole.mutate({
                            userId: m.user_id,
                            role: e.target.value as WorkspaceRole,
                          })
                        }
                        disabled={updateRole.isLoading}
                        className={cn(
                          "h-8 rounded-md border border-ocean-100 bg-white px-2 text-xs",
                          "focus:outline-none focus:ring-2 focus:ring-ocean-400"
                        )}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {t(WORKSPACE_ROLE_KEY[r])}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge tone={WORKSPACE_ROLE_TONE[m.role] ?? "neutral"}>
                        {resolveRoleLabel(m.role)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden tablet:table-cell text-xs text-neutral-600">
                    {formatDateID(m.joined_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManageMembers && m.user_id !== user?.user_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void removeMember.mutate(m.user_id)}
                        disabled={removeMember.isLoading}
                        aria-label={t(
                          "workspaces.detail.removeMemberAriaLabel",
                          { email: m.user_email }
                        )}
                      >
                        <Trash2 size={14} />
                        {t("workspaces.detail.removeMember")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <AddMemberDialog
        workspaceId={addMemberOpen ? workspace.id : null}
        existingMemberIds={memberItems.map((m) => m.user_id)}
        onClose={() => setAddMemberOpen(false)}
        onAdded={() => {
          setAddMemberOpen(false);
          void members.refetch();
          void detail.refetch();
        }}
      />

      {archiveOpen && (
        <ArchiveWorkspaceDialog
          workspace={workspace}
          allWorkspaces={workspaces}
          onClose={() => setArchiveOpen(false)}
          onArchived={async () => {
            setArchiveOpen(false);
            await refreshWorkspaces();
            navigate(ROUTES.WORKSPACES);
          }}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
